/**
 * Reports > Quality (user, 2026-10-28): the QC module's reports, in the user's list order. The legacy
 * ones are SeedTrac's QC reports column for column (wmsd qc1/, read view-only 2026-10-28; see
 * docs/reference/seedtrac-qc-reports-live-2026-10-28.md); Periodical Arrival is new, with no legacy
 * screen. Each returns the shared `ReportResult`, so the screen, Excel and PDF are the warehouse
 * reports' own builders.
 */
import { ARRIVAL_TYPES, ARRIVAL_TYPE_KEYS } from "./arrival-reports";
import type {
  ReportCellFormat,
  ReportColumn,
  ReportDef,
} from "./warehouse-reports";

/** The reports built here, in the user's list order (#3, #4 and #13 open existing pages). */
export const QUALITY_REPORT_SLUGS = [
  "sample-pending",
  "result-pending",
  "germ-first-count-list",
  "germ-evaluation-list",
  "substandard",
  "soft-release",
  "super-soft-release",
  "blending",
  "stock",
  "sample-collection",
  "stock-status",
  "density",
  "testing-data",
  "pp-testing",
  "moisture-testing",
  "periodical-arrival",
] as const;
export type QualityReportSlug = (typeof QUALITY_REPORT_SLUGS)[number];
export type QualityReportKey = `qc-${QualityReportSlug}`;

export const isQualityReportSlug = (s: string): s is QualityReportSlug =>
  (QUALITY_REPORT_SLUGS as readonly string[]).includes(s);
export const qualityReportKey = (slug: QualityReportSlug): QualityReportKey =>
  `qc-${slug}`;

const c = (
  key: string,
  label: string,
  format: ReportCellFormat,
  more: Partial<Omit<ReportColumn, "key" | "label" | "format">> = {}
): ReportColumn => ({
  key,
  label,
  format,
  align:
    format === "int" || format === "qty" || format === "pct"
      ? "right"
      : format === "text"
        ? "left"
        : "center",
  ...more,
});

const SERIAL = c("serial", "#", "int", { align: "center" });
const CROP = c("crop", "Crop", "text");
const VARIETY = c("variety", "Variety", "text");
const LOT = c("lotNumber", "Lot No.", "text");

const qty = (key: string, label: string, group?: string, total = true) =>
  c(key, label, "qty", { ...(group ? { group } : {}), total });
const int = (key: string, label: string, group?: string, total = false) =>
  c(key, label, "int", { ...(group ? { group } : {}), total });
const pct = (key: string, label: string, group?: string) =>
  c(key, label, "pct", group ? { group } : {});
const date = (key: string, label: string, group?: string) =>
  c(key, label, "date", group ? { group } : {});
const text = (key: string, label: string, group?: string) =>
  c(key, label, "text", group ? { group } : {});
const mid = (key: string, label: string, group?: string) =>
  c(key, label, "text", { align: "center", ...(group ? { group } : {}) });

/** The ten columns every legacy testing-data report opens with. */
const SAMPLE_HEAD = [
  SERIAL,
  date("dosr", "DOSR"),
  CROP,
  VARIETY,
  LOT,
  qty("qty", "Total Qty (kg)"),
  mid("qcTests", "QC Tests"),
  text("sampleNo", "Sample no."),
  date("dosc", "DOSC"),
  text("docRefNo", "Doc. Ref. No."),
];

/** The legacy Seed Stage select: every stage, or one. */
const SEED_STAGES = [
  { value: "all", label: "All stages" },
  { value: "raw", label: "Raw" },
  { value: "condition", label: "Condition" },
  { value: "pack", label: "Pack" },
] as const;

const LOTS_TOO_MANY = "Too many lots. Shorten the dates or choose a crop.";
const SAMPLES_TOO_MANY =
  "Too many samples. Shorten the dates or choose a crop.";

/** Periodical Arrival's "Arrival Type" picker: every type, or one. */
export const PERIODICAL_ARRIVAL_KINDS = [
  { value: "all", label: "All arrival types" },
  ...ARRIVAL_TYPE_KEYS.map((t) => ({
    value: t,
    label: ARRIVAL_TYPES[t].label,
  })),
] as const;

export const QUALITY_REPORTS: {
  [K in QualityReportKey]: ReportDef & { key: K };
} = {
  // 1 · qc1/report_qcsamp.php
  "qc-sample-pending": {
    key: "qc-sample-pending",
    menu: "Report - QC Sample Pending List",
    title: "QC Sample Pending List",
    asOn: true,
    kind: "flat",
    filters: ["crop", "variety"],
    choices: [
      {
        param: "mode",
        label: "Report Type",
        options: [
          { value: "cvsloc", label: "Crop-Variety SLOC wise" },
          { value: "sloc", label: "SLOC wise" },
        ],
      },
    ],
    columns: [
      SERIAL,
      date("dosr", "DOSR"),
      CROP,
      VARIETY,
      LOT,
      qty("qty", "Total Qty"),
      text("stageSloc", "Stage, NoB/Qty, SLOC"),
      mid("qcTests", "QC Tests"),
      mid("oprCode", "Opr Code"),
      text("sampleNo", "Sample No."),
    ],
    tooMany: SAMPLES_TOO_MANY,
    note: "Samples requested and not yet collected. SLOC wise lists them in bin order, as the sampler walks.",
  },
  // 2 · qc1/report_uqc.php
  "qc-result-pending": {
    key: "qc-result-pending",
    menu: "QC Result Pending Report",
    title: "QC Result Pending Report",
    asOn: true,
    kind: "flat",
    filters: ["asOnDate", "crop", "variety"],
    choices: [
      {
        param: "kind",
        label: "QC Test Instance",
        options: [
          { value: "both", label: "UT and RT" },
          { value: "ut", label: "UT" },
          { value: "rt", label: "RT" },
        ],
      },
      {
        param: "mode",
        label: "Report Type",
        options: [
          { value: "all", label: "All TIP" },
          { value: "edor", label: "EDOR Matured" },
        ],
      },
    ],
    columns: [
      SERIAL,
      CROP,
      VARIETY,
      LOT,
      int("bags", "NoB", undefined, true),
      qty("qty", "Qty"),
      mid("qcStatus", "QC Status"),
      date("dosr", "DOSR"),
      date("dosc", "DOSC"),
      date("edor", "EDOR"),
      text("sampleNo", "Sample No."),
    ],
    tooMany: SAMPLES_TOO_MANY,
    note: "All TIP: every test in progress, its Expected Date of Result (EDOR) passed or not. EDOR Matured: the EDOR has passed and the result is not yet updated. EDOR is the germination setup day plus the crop's Final Count day; a sample with no germination test expects its result the day it was collected.",
  },
  // The lab's printed Daily 1st Count List (user, 2026-11-01, sheet photo IMG-20261031-WA0001):
  // germination trays whose First Count falls on the day, or fell earlier and is still not counted.
  "qc-germ-first-count-list": {
    key: "qc-germ-first-count-list",
    menu: "Daily 1st Count List",
    title: "Daily 1st Count List",
    asOn: true,
    kind: "flat",
    filters: ["asOnDate", "crop", "variety"],
    columns: [
      SERIAL,
      CROP,
      mid("sampleType", "Sample type"),
      mid("srfNo", "SRF"),
      date("bpGkd", "GKD", "BP"),
      date("bpEdor", "EDOR", "BP"),
      date("firstCountOn", "1st Count Date"),
      mid("due", "Due"),
      int("total", "Total", undefined, true),
      text("evaluatedBy", "Evaluated by"),
      text("receivingScan", "Receiving SCAN"),
      text("remark", "Remark"),
    ].map((col) =>
      ["evaluatedBy", "receivingScan", "remark"].includes(col.key)
        ? { ...col, writeIn: true as const }
        : col
    ),
    tooMany: SAMPLES_TOO_MANY,
    note: "Germination trays whose 1st Count is due on the day (setup day plus the crop's First Count day), and earlier ones still not counted (Due: Late). BP is the SGT, GKD the day it was set up, EDOR its expected result day. Total is the number of samples. Evaluated by, Receiving SCAN and Remark are left blank to fill by hand.",
  },
  // The lab's printed Daily Evaluation List (user, 2026-11-01, sheet photo IMG-20261031-WA0000):
  // trays whose Final Count falls on the day, or fell earlier and is still not counted.
  "qc-germ-evaluation-list": {
    key: "qc-germ-evaluation-list",
    menu: "Daily Evaluation List",
    title: "Daily Evaluation List",
    asOn: true,
    kind: "flat",
    filters: ["asOnDate", "crop", "variety"],
    columns: [
      SERIAL,
      CROP,
      mid("sampleType", "Sample type"),
      mid("srfNo", "SRF"),
      date("bpGkd", "GKD", "BP"),
      date("bpEdor", "EDOR", "BP"),
      date("feGkd", "GKD", "FE"),
      date("feEdor", "EDOR", "FE"),
      mid("due", "Due"),
      int("total", "Total", undefined, true),
      text("doe", "DOE"),
      text("evaluatedBy", "Evaluated by"),
      text("receivingScan", "Receiving SCAN"),
    ].map((col) =>
      ["doe", "evaluatedBy", "receivingScan"].includes(col.key)
        ? { ...col, writeIn: true as const }
        : col
    ),
    tooMany: SAMPLES_TOO_MANY,
    note: "Germination trays whose Final Count (evaluation) is due on the day (setup day plus the crop's Final Count day), and earlier ones still not counted (Due: Late). BP is the SGT, FE the FGT; GKD the day each was set up, EDOR its expected result day. Total is the number of samples. DOE, Evaluated by and Receiving SCAN are left blank to fill by hand.",
  },
  // 5 · qc1/report_rawsscrop.php
  "qc-substandard": {
    key: "qc-substandard",
    menu: "Crop Variety wise Substandard Seed Report",
    title: "Crop Variety wise Substandard Seed Report",
    asOn: false,
    kind: "sectioned",
    filters: ["period", "crop", "variety"],
    choices: [{ param: "seedStage", label: "Stage", options: SEED_STAGES }],
    columns: [
      SERIAL,
      LOT,
      mid("stage", "Stage"),
      int("bags", "NoB", undefined, true),
      qty("qty", "Qty"),
      c("slocs", "SLOC", "slocs"),
      mid("qcStatus", "QC status"),
      pct("moisturePct", "Moist %"),
      pct("germinationPct", "Germ %"),
      date("testedOn", "DoT"),
      mid("gotStatus", "GOT Status"),
      pct("geneticPurityPct", "Genetic Purity %"),
      date("dogr", "DOGR"),
      mid("seedStatus", "Seed Status"),
    ],
    tooMany: LOTS_TOO_MANY,
    note: "Lots in stock whose QC or GOT failed. The period, when set, is the date of the test.",
  },
  // 6 · qc1/report_sfr.php
  "qc-soft-release": {
    key: "qc-soft-release",
    menu: "Soft Release Report",
    title: "Soft Release Report",
    asOn: false,
    kind: "sectioned",
    filters: ["period", "crop", "variety"],
    columns: [
      SERIAL,
      LOT,
      int("bags", "NoB", undefined, true),
      qty("qty", "Qty"),
      date("srDate", "SR Date"),
      mid("srStatus", "SR Status"),
      text("remarks", "Remarks"),
    ],
    tooMany: LOTS_TOO_MANY,
    note: "All dates is SeedTrac's As on Date. SR Status is what the lot was released for; Remarks says whether the release is still in progress.",
  },
  // 7 · qc1/report_ssfr.php
  "qc-super-soft-release": {
    key: "qc-super-soft-release",
    menu: "Super Soft Release Report",
    title: "Super Soft Release Report",
    asOn: false,
    kind: "sectioned",
    filters: ["period", "crop", "variety"],
    columns: [
      SERIAL,
      LOT,
      int("bags", "NoB", undefined, true),
      qty("qty", "Qty"),
      date("srDate", "SR Date"),
      mid("srStatus", "SR Status"),
      text("remarks", "Remarks"),
    ],
    tooMany: LOTS_TOO_MANY,
    note: "All dates is SeedTrac's As on Date. SR Status is what the lot was released for; Remarks says whether the release is still in progress.",
  },
  // 8 · qc1/report_blending.php
  "qc-blending": {
    key: "qc-blending",
    menu: "Periodical Blending Report",
    title: "Periodical Blending Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    choices: [
      {
        param: "kind",
        label: "Variety Type",
        options: [
          { value: "all", label: "All variety types" },
          { value: "hybrid", label: "Hybrid" },
          { value: "op", label: "OP" },
        ],
      },
    ],
    columns: [
      CROP,
      VARIETY,
      date("blendedOn", "Blending Date"),
      text("blendLot", "Blended Lot No.", "Blended Lot"),
      qty("blendQty", "Qty", "Blended Lot"),
      c("blendSlocs", "SLOC", "slocs", { group: "Blended Lot" }),
      text("lotNumber", "Constituent Lot No.", "Constituent Lot"),
      qty("qty", "Qty", "Constituent Lot"),
      c("slocs", "SLOC", "slocs", { group: "Constituent Lot" }),
      mid("qcStatus", "QC"),
      date("testedOn", "DoT"),
      pct("germinationPct", "Germ.%"),
      mid("gotStatus", "GOT"),
      date("dogr", "DoGR"),
    ],
    tooMany: LOTS_TOO_MANY,
    note: "One row per constituent lot; the blended lot is named on its first. Constituent SLOC is where it was picked from.",
  },
  // 9 · qc1/stock_report.php (and stock_report_new.php)
  "qc-stock": {
    key: "qc-stock",
    menu: "Stock Report",
    title: "Stock Report",
    asOn: true,
    kind: "sectioned",
    filters: ["crop", "variety"],
    columns: [
      SERIAL,
      LOT,
      int("rawBags", "NoB", "Raw Seed", true),
      qty("rawQty", "Qty", "Raw Seed"),
      int("condBags", "NoB", "Condition Seed", true),
      qty("condQty", "Qty", "Condition Seed"),
      text("ups", "UPS", "Pack Seed"),
      int("nop", "NoP", "Pack Seed", true),
      c("nomp", "NoMP", "int", { group: "Pack Seed", blank: true }),
      qty("packQty", "Qty", "Pack Seed"),
      c("srBags", "NoB", "int", { group: "Sales Return", blank: true }),
      c("srQty", "Qty", "qty", { group: "Sales Return", blank: true }),
      int("bags", "NoB", "Total", true),
      qty("qty", "Qty", "Total"),
      c("slocs", "SLOC", "slocs", { group: "Total" }),
      pct("germinationPct", "Germ %", "Quality Status"),
      mid("qcStatus", "QC", "Quality Status"),
      date("testedOn", "DoT", "Quality Status"),
      mid("gotStatus", "GOT", "Quality Status"),
      date("dogr", "DoGR", "Quality Status"),
      text("arrivalYearPlant", "Arrival Year & Plant"),
    ],
    tooMany: "Too many lots. Choose a crop or variety.",
    note: "Every lot in a bin at the plant. NoMP (master packs) and Sales Return have no stock of their own in NeXseed and print blank.",
  },
  // 10 · qc1/report_samplecollection.php
  "qc-sample-collection": {
    key: "qc-sample-collection",
    menu: "Sample Collection Report",
    title: "Sample Collection List",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    columns: [
      SERIAL,
      date("dosr", "DOSR"),
      CROP,
      VARIETY,
      LOT,
      qty("qty", "Total Qty"),
      mid("qcTests", "QC Tests"),
      text("sampleNo", "Sample No."),
      date("dosc", "Sample Collection Date"),
      text("srfNo", "SRF No./QC Ref. No."),
      text("remark", "Remark"),
    ],
    tooMany: SAMPLES_TOO_MANY,
    note: "Samples collected in the period (by Sample Collection Date).",
  },
  // 11 · qc1/stock_status_report.php
  "qc-stock-status": {
    key: "qc-stock-status",
    menu: "Stock Status Report",
    title: "Stock Status Report",
    asOn: true,
    kind: "aggregated",
    filters: ["crop", "variety"],
    columns: [
      SERIAL,
      CROP,
      VARIETY,
      qty("qty", "Total Stock"),
      qty("qcOk", "OK", "GOT OK and Germination based qty. (kg)"),
      qty("qcBl", "BL", "GOT OK and Germination based qty. (kg)"),
      qty("qcUt", "UT", "GOT OK and Germination based qty. (kg)"),
      qty("qcRt", "RT", "GOT OK and Germination based qty. (kg)"),
      qty("qcFail", "Fail", "GOT OK and Germination based qty. (kg)"),
      qty("gotBl", "GOT-BL", "GOT Based qty. (kg)"),
      qty("gotUt", "GOT-UT", "GOT Based qty. (kg)"),
      qty("gotFail", "GOT-Fail", "GOT Based qty. (kg)"),
    ],
    tooMany: "Too many lots. Choose a crop or variety.",
    note: "Stock in bins at the plant. A lot whose GOT is OK (or not required) counts under its germination result; any other GOT counts under its GOT status, so the columns add up to Total Stock.",
  },
  // 12 · qc1/report_density.php
  "qc-density": {
    key: "qc-density",
    menu: "Periodical Density Report",
    title: "Periodical Density Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    columns: [
      SERIAL,
      date("arrivedOn", "Arrival Date"),
      date("processedOn", "Processing Date"),
      CROP,
      VARIETY,
      text("spCodes", "SP Codes"),
      LOT,
      qty("rawQty", "Raw Seed Qty"),
      qty("condQty", "Condition Seed Qty"),
      qty("lossQty", "Condition Loss"),
      pct("lossPct", "Condition Loss %"),
      text("blendLot", "Blended Lot No."),
      c("rawDensity", "Density - Raw", "qty"),
      c("condDensity", "Density - Condition", "qty"),
      qty("rmQty", "Remnant qty (kg)"),
      pct("rmPct", "Remmamt %"),
    ],
    tooMany: LOTS_TOO_MANY,
    note: "Every lot a Processing Slip submitted in the period, with the raw lot's and the condition lot's density.",
  },
  // 14 · qc1/report_testingmasterfile.php
  "qc-testing-data": {
    key: "qc-testing-data",
    menu: "Periodical Testing Data Report",
    title: "Periodical Testing Data Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    columns: [
      ...SAMPLE_HEAD,
      pct("moisturePct", "Moisture (%)", "Moisture Test Report"),
      date("moistureOn", "Date of Test", "Moisture Test Report"),
      mid("ppResult", "PP Result", "Physical Purity Test Report"),
      date("ppOn", "Date of test", "Physical Purity Test Report"),
      mid("germType", "Germination Test Type"),
      date("gkd", "GKD"),
      date("edor", "EDOR"),
      int("sgtReps", "No of replication", "Standard Germination Test (SGT)"),
      pct(
        "sgtNormal",
        "Normal Seedlings (%)",
        "Standard Germination Test (SGT)"
      ),
      pct(
        "sgtAbnormal",
        "Abormal Seedlings (%)",
        "Standard Germination Test (SGT)"
      ),
      pct("sgtDead", "Dead Seeds (%)", "Standard Germination Test (SGT)"),
      pct("sgtHard", "Hard/FUG Seeds (%)", "Standard Germination Test (SGT)"),
      mid("sgtVigour", "Vigor", "Standard Germination Test (SGT)"),
      date("sgtOn", "Date of test", "Standard Germination Test (SGT)"),
      int("fgtReps", "No of replication", "Field Germination Test (FGT)"),
      pct("fgtNormal", "Normal Seedlings (%)", "Field Germination Test (FGT)"),
      pct(
        "fgtAbnormal",
        "Abormal Seedlings (%)",
        "Field Germination Test (FGT)"
      ),
      pct("fgtDead", "Dead Seeds (%)", "Field Germination Test (FGT)"),
      mid("fgtVigour", "Vigor", "Field Germination Test (FGT)"),
      date("fgtOn", "Date of Test", "Field Germination Test (FGT)"),
      pct("germinationPct", "Updated result"),
      date("resultOn", "Result Submit Date"),
      mid("qcStatus", "QC Status"),
      text("remarks", "Remarks"),
      c("slocs", "Working", "slocs", { group: "SLOC" }),
      text("guardSloc", "Guard", "SLOC"),
      date("discardBy", "Guard Sample Discard Schedule"),
      mid("softReleased", "Soft Released Lots For Dispatch"),
      date("arrivedOn", "Arrival Date", "Arrival Details"),
      text("spFemale", "SP Code Female", "Arrival Details"),
      text("spMale", "SP Code Male", "Arrival Details"),
      date("harvestedOn", "Harvest Date", "Arrival Details"),
    ],
    tooMany: SAMPLES_TOO_MANY,
    note: "Every sample requested in the period, with its verified Moisture, PP and Germination tests and its QC result. Working SLOC is where the lot is now; Guard is where its guard sample is kept.",
  },
  // 15 · qc1/report_pptestingfile.php
  "qc-pp-testing": {
    key: "qc-pp-testing",
    menu: "Periodical PP Testing Data Report",
    title: "Periodical Physical Purity Testing Data Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    columns: [
      ...SAMPLE_HEAD,
      c("sampleWtG", "Sample Weight", "qty"),
      c("pureG", "Weight (g)", "qty", { group: "Pure Seed" }),
      pct("purePct", "%", "Pure Seed"),
      c("inertG", "Weight (g)", "qty", { group: "Inert Matter" }),
      pct("inertPct", "%", "Inert Matter"),
      c("lightG", "Weight (g)", "qty", { group: "Light Seeds" }),
      pct("lightPct", "%", "Light Seeds"),
      c("ocsG", "Weight (g)", "qty", { group: "Other Crop Seeds" }),
      int("ocsNo", "No of Seeds", "Other Crop Seeds"),
      int("ocsPerKg", "Nos/Kg", "Other Crop Seeds"),
      c("odvG", "Weight (g)", "qty", {
        group: "Other Distinguishable Variety",
      }),
      int("odv1010", "1010", "Other Distinguishable Variety"),
      int("odvFine", "Fine Grain", "Other Distinguishable Variety"),
      int("odvBold", "Bold Grain", "Other Distinguishable Variety"),
      int("odvLong", "Long Grain", "Other Distinguishable Variety"),
      int("odvOther", "Other Type", "Other Distinguishable Variety"),
      int("odvNo", "No of Seeds", "Other Distinguishable Variety"),
      int("odvPerKg", "Nos/Kg", "Other Distinguishable Variety"),
      c("discolouredG", "Weight (g)", "qty", { group: "Discoloured Seeds" }),
      pct("discolouredPct", "%", "Discoloured Seeds"),
      c("cutG", "Weight (g)", "qty", { group: "Cut Seeds" }),
      pct("cutPct", "%", "Cut Seeds"),
      c("smallG", "Weight (g)", "qty", { group: "Small Seeds" }),
      pct("smallPct", "%", "Small Seeds"),
      c("roughG", "Weight (g)", "qty", { group: "Rough Seeds" }),
      pct("roughPct", "%", "Rough Seeds"),
      c("whiteG", "Weight (g)", "qty", { group: "White Seeds" }),
      pct("whitePct", "%", "White Seeds"),
      c("pinholeG", "Weight (g)", "qty", { group: "Pin Holes" }),
      int("pinholeNo", "No of Seeds", "Pin Holes"),
      int("pinholePerKg", "Nos/Kg", "Pin Holes"),
      c("germinatedG", "Weight (g)", "qty", { group: "Germinated Seeds" }),
      int("germinatedNo", "No of Seeds", "Germinated Seeds"),
      int("germinatedPerKg", "Nos/Kg", "Germinated Seeds"),
      mid("ppResult", "PP Result"),
      date("ppOn", "PP Result Date"),
    ],
    tooMany: SAMPLES_TOO_MANY,
    note: "Each sample's latest submitted Physical Purity test in the period (by DOSR). % and Nos/Kg are worked out from the sample weight.",
  },
  // 16 · qc1/report_mtestingfile.php
  "qc-moisture-testing": {
    key: "qc-moisture-testing",
    menu: "Periodical Moisture Testing Data Report",
    title: "Periodical Moisture Test Data Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    columns: [
      ...SAMPLE_HEAD,
      pct("moisturePct", "Moisture %", "Moisture Test Report"),
      date("moistureOn", "Result date", "Moisture Test Report"),
      ...[1, 2, 3, 4].map((r) =>
        c(`m1r${r}`, `M1 R${r}`, "qty", { group: "Hot Air Oven Method" })
      ),
      ...[1, 2, 3, 4].map((r) =>
        c(`m2r${r}`, `M2 R${r}`, "qty", { group: "Hot Air Oven Method" })
      ),
      date("m2On", "M2 Date", "Hot Air Oven Method"),
      ...[1, 2, 3, 4].map((r) =>
        c(`m3r${r}`, `M3 R${r}`, "qty", { group: "Hot Air Oven Method" })
      ),
      date("m3On", "M3 Date", "Hot Air Oven Method"),
      ...[1, 2, 3, 4].map((r) =>
        pct(`ovenRep${r}`, `R${r} %`, "Hot Air Oven Method")
      ),
      pct("ovenPct", "Oven %", "Hot Air Oven Method"),
      ...[1, 2, 3].map((r) => pct(`meterR${r}`, `R${r}`, "Moisture Meter")),
      date("meterOn", "Date", "Moisture Meter"),
      pct("meterPct", "Moist %", "Moisture Meter"),
      ...[1, 2, 3].map((r) => pct(`supR${r}`, `Sup. R${r}`, "Moisture Meter")),
      date("supOn", "Sup. Date", "Moisture Meter"),
      pct("supPct", "Sup. Moist %", "Moisture Meter"),
    ],
    tooMany: SAMPLES_TOO_MANY,
    note: "Each sample's latest Moisture test in the period (by DOSR). Hot Air Oven: M1 the empty container, M2 with seed, M3 after the oven, for each replication (R1–R4), and the replication's and the oven's moisture %. Moisture Meter: the analyst's readings, then the supervisor's check (Sup.).",
  },
  // 18 · new (no legacy screen)
  "qc-periodical-arrival": {
    key: "qc-periodical-arrival",
    menu: "Periodical Arrival Report",
    title: "Periodical Arrival Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    choices: [
      {
        param: "kind",
        label: "Arrival Type",
        options: PERIODICAL_ARRIVAL_KINDS,
      },
    ],
    // SeedTrac's Periodical Arrival Report, column for column (user, 2026-10-31), with SP-F and
    // SP-M under one SP Code heading as F | M.
    columns: [
      SERIAL,
      c("arrivedOn", "Date of Arrival", "date"),
      CROP,
      c("spFemale", "F", "text", { group: "SP Code", align: "center" }),
      c("spMale", "M", "text", { group: "SP Code", align: "center" }),
      c("stage", "Stage", "text", { align: "center" }),
      c("frnNo", "FRN No.", "text"),
      LOT,
      int("bags", "NoB"),
      qty("qty", "Qty", undefined, false),
      text(
        "productionPerson",
        "Production Personnel",
        "Production Information"
      ),
      text("organiser", "Organiser", "Production Information"),
      text("farmer", "Farmer", "Production Information"),
      text("state", "State", "Production Information"),
      text(
        "productionLocation",
        "Production Location",
        "Production Information"
      ),
      date("harvestedOn", "Harvest Date", "Production Information"),
      date("driedOn", "Drying date", "Drying Information"),
      qty("dryInQty", "Drying input Quantity", "Drying Information", false),
      qty("dryOutQty", "Drying Output Quantity", "Drying Information", false),
      date("blendedOn", "Blending date", "Blending Information"),
      qty("blendQty", "Blending Qty", "Blending Information", false),
      text("blendStatus", "Blending status", "Blending Information"),
      qty("rawPicked", "Raw Qty (Picked)", "Processing", false),
      qty("condQty", "Condition Qty", "Processing", false),
      qty("tclQty", "Total Condition Loss Qty", "Processing", false),
      pct("tclPct", "TCL %", "Processing"),
      qty("rawBalance", "Raw Qty (Balance)", "Processing", false),
      c("qcStatus", "QC Status", "text", { group: "Quality", align: "center" }),
      pct("germinationPct", "Germination %", "Quality"),
      date("testedOn", "DOT", "Quality"),
      c("gotStatus", "GOT Status", "text", {
        group: "Quality",
        align: "center",
      }),
      pct("gpPct", "Genetic Purity %", "Quality"),
      date("dogr", "DOGR", "Quality"),
    ],
    tooMany: "Too many lots. Shorten the dates or choose a crop.",
    note: "Every arrival line received in the period, of every arrival type unless one is chosen. Maize Cob Arrival lists the cob trucks booked at the dryers the login may see. Drying is the lot's Drying Slips; Blending the blends it went into; Processing the raw seed picked onto Processing Slips from it (or from a blend it went into, at its share), the condition seed made, their RM + IM + PL loss, and the raw seed still in bins. QC Status, Germination % and DOT are its latest lab result; GOT Status, Genetic Purity % and DOGR its GOT result.",
  },
};

/** Each report's line under its name in the menu and the hub. */
const QUALITY_REPORT_HINTS: Record<QualityReportSlug, string> = {
  "sample-pending":
    "Samples requested and not yet collected, crop-variety or SLOC wise",
  "result-pending":
    "Tests in progress as on a date, UT or RT, with their expected result date",
  "germ-first-count-list":
    "Germination trays due for their 1st Count on a day, late ones too, to print for the lab",
  "germ-evaluation-list":
    "Germination trays due for evaluation (Final Count) on a day, late ones too, to print for the lab",
  substandard: "Lots in stock whose QC or GOT failed, stage by stage",
  "soft-release": "Lots soft-released in a period or as on today",
  "super-soft-release": "Lots super-soft-released in a period or as on today",
  blending: "Blended lots and the lots that went into them, with QC and GOT",
  stock:
    "Every lot at the plant: raw, condition and pack, with its quality status",
  "sample-collection": "Samples collected in a period, with SRF number",
  "stock-status":
    "Stock by crop and variety, split by germination result and GOT",
  density: "Processed lots: raw against condition qty, loss and density",
  "testing-data":
    "Every test of every sample in a period: moisture, PP, SGT, FGT and result",
  "pp-testing": "Physical Purity readings of each sample in a period",
  "moisture-testing":
    "Hot air oven and moisture meter readings of each sample in a period",
  "periodical-arrival":
    "Every arrival in a period, with its production, drying, blending, processing, QC and GOT",
};

/** The Quality group's menu, in the user's order (2026-10-28). */
export const QUALITY_REPORT_LIST = QUALITY_REPORT_SLUGS.map((slug) => ({
  slug,
  def: QUALITY_REPORTS[qualityReportKey(slug)],
  hint: QUALITY_REPORT_HINTS[slug],
}));
